import { useCallback, useEffect, useState } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { processFinishTo } from "../utils";

const containerStyle = {
    minWidth: window.innerWidth < 850 ? "95vw" : "800px",
    height: "600px",
};

const options = { disableDefaultUI: true, clickableIcons: false };

export default function GMap({ type, caption, data, googleMapsApiKey, setModal, locations, setLocations, id }) {
    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey,
    });

    const [selected, setSelected] = useState(null);
    const [preparedData, setPreparedData] = useState([]);
    const [center, setCenter] = useState({ lat: 48.54, lng: 22.99 }); //{lat: 48.4423678,lng: 22.7211456};

    const prepareOrdersData = useCallback(async () => {
        if (!isLoaded || !data?.length) return [];

        const newLocations = { ...locations };
        if (type === "orders" && !newLocations.orders) newLocations.orders = {};

        const geocoder = window.google?.maps ? new window.google.maps.Geocoder() : null;
        const result = [];

        for (const item of data) {
            let coords;

            if (type === "orders") {
                // Використовуємо кеш з locations
                if (newLocations.orders[item.address]) {
                    coords = newLocations.orders[item.address];
                } else if (geocoder && item?.address) {
                    try {
                        coords = await new Promise((resolve, reject) => {
                            geocoder.geocode({ address: item.address }, (results, status) => {
                                if (status === "OK" && results[0]) {
                                    const loc = results[0].geometry.location;
                                    resolve({ lat: loc.lat(), lng: loc.lng() });
                                } else {
                                    reject(status);
                                }
                            });
                        });
                        newLocations.orders[item.address] = coords;
                    } catch (err) {
                        console.error(`Не вдалося геокодувати ${item.address}:`, err);
                        continue;
                    }
                }
            }
            result.push({
                id: item.id,
                sum: item.sum,
                author: item.author.name,
                //label: (parseInt(item.id || 0) % 1000) + "",
                label: (parseInt(item.phone || 0) % 1000) + "",
                address: item.address,
                location: coords,
                finish_to: item.finish_to,
            });
        }

        if (type === "orders") setLocations(newLocations);

        // Оновлюємо центр карти
        const validCoords = result.filter((o) => o.location);
        if (validCoords.length === 1) {
            setCenter(validCoords[0].location);
        } else if (validCoords.length > 1) {
            const latSum = validCoords.reduce((sum, o) => sum + o.location.lat, 0);
            const lngSum = validCoords.reduce((sum, o) => sum + o.location.lng, 0);
            setCenter({ lat: latSum / validCoords.length, lng: lngSum / validCoords.length });
        }

        return result;
    }, [isLoaded, data, type, locations, setLocations]);

    useEffect(() => {
        if (isLoaded && data?.length) {
            if (type === "orders") {
                prepareOrdersData().then(setPreparedData);
            } else if (type === "users") {
                const result = data.map((item) => ({
                    id: item.id,
                    label: item.name.slice(0, 3),
                    name: item.name,
                    location: item.location,
                    last_upd: item.last_upd,
                }));
                setPreparedData(result);
                // Оновлюємо центр карти
                const validCoords = result.filter((o) => o.location);
                if (validCoords.length === 1) {
                    setCenter(validCoords[0].location);
                } else if (validCoords.length > 1) {
                    const latSum = validCoords.reduce((sum, o) => sum + o.location.lat, 0);
                    const lngSum = validCoords.reduce((sum, o) => sum + o.location.lng, 0);
                    setCenter({ lat: latSum / validCoords.length, lng: lngSum / validCoords.length });
                }
            }
        }
    }, [isLoaded, data, type, prepareOrdersData]);

    // Drag handlers для модального вікна
    let pos1 = 0,
        pos2 = 0,
        pos3 = 0,
        pos4 = 0;

    const dragMouseDown = (e) => {
        e = e || window.event;
        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        document.onmousemove = elementDrag;
    };

    const elementDrag = (e) => {
        const elmnt = document.getElementById("modalWnd" + id);
        e = e || window.event;
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        if (pos2 !== 0) elmnt.style.top = elmnt.offsetTop - pos2 + "px";
        if (pos1 !== 0) elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
    };

    const closeDragElement = () => {
        document.onmouseup = null;
        document.onmousemove = null;
    };

    return isLoaded && preparedData.length > 0 ? (
        <div className="modal">
            <div id={"modalWnd" + id} className="window">
                <div className="no-print" onMouseDown={dragMouseDown} style={{ flexDirection: "row", display: "flex", justifyContent: "flex-start" }}>
                    <div className="caption">{caption}</div>
                    <div className="closebtn" onClick={() => setModal("")} />
                </div>
                <div className="content">
                    <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={13} options={options}>
                        {preparedData.map(
                            (item) =>
                                item.location && (
                                    <Marker
                                        key={item.id}
                                        position={item.location}
                                        label={{
                                            text: item.label,
                                            color: "white",
                                            fontSize: "12px",
                                            fontWeight: "bold",
                                        }}
                                        onClick={() => setSelected(item)}
                                    />
                                )
                        )}
                        {selected && type === "orders" && (
                            <InfoWindow position={selected.location} onCloseClick={() => setSelected(null)}>
                                <div>
                                    Замовлення №<b>{selected.id}</b> <br />
                                    Автор: <b>{selected.author}</b> <br />
                                    Адреса: <b>{selected.address}</b> <br />
                                    {selected?.finish_to && selected.finish_to.includes("#") ? (
                                        <>
                                            Доставити через {processFinishTo(selected.finish_to.substring(0, selected.finish_to.length - 3))} <br />
                                        </>
                                    ) : (
                                        <>
                                            Доставити до {selected.finish_to} {processFinishTo(selected.finish_to)} <br />
                                        </>
                                    )}
                                    Сума: <b>{parseFloat(selected.sum).toFixed(2)} грн.</b> <br />
                                </div>
                            </InfoWindow>
                        )}
                        {selected && type === "users" && (
                            <InfoWindow position={selected.location} onCloseClick={() => setSelected(null)}>
                                <div>
                                    ID: <b>{selected.id}</b> <br />
                                    Ім'я: <b>{selected.name}</b> <br />
                                    Оновлено: <b>{new Date(selected.last_upd).toLocaleString("uk-UA")}</b>
                                </div>
                            </InfoWindow>
                        )}
                    </GoogleMap>
                </div>
            </div>
        </div>
    ) : (
        <div>Завантаження карти...</div>
    );
}
